import React, { useRef, useEffect, useState } from "react";
import { ImageItem, LayerItem } from "@exibra/shared";
import { Image, Transformer } from "react-konva";
import { KonvaEventObject } from "konva/lib/Node";
import Konva from "konva";
import { useKonvaSnapping } from "@/hooks/use-konva-snapping";

export interface ImageElementEditorProps {
    imageItem: ImageItem;
    onChange: (imageItem: ImageItem) => void;
    setSelectedId: (id: string | null) => void;
    addToHistory: () => void;
    setLayers: React.Dispatch<React.SetStateAction<LayerItem[]>>;
    layers: LayerItem[];
    selectedId: string | null;
    image: HTMLImageElement;
}

export const ImageElementEditor: React.FC<ImageElementEditorProps> = ({
    imageItem,
    onChange,
    setSelectedId,
    addToHistory,
    setLayers,
    selectedId,
    image,
}) => {
    const transformerRef = useRef<Konva.Transformer>(null);
    const imageRef = useRef<Konva.Image>(null);
    const [isDragging, setIsDragging] = useState(false);

    const {
        handleDragging: handleSnapping,
        handleResizing: handleSnappingResize,
        handleResizeEnd: handleSnappingResizeEnd,
        handleDragEnd: handleSnappingDragEnd,
    } = useKonvaSnapping({
        snapRange: 10,
        guidelineColor: "red",
        guidelineDash: true,
        showGuidelines: true,
        guidelineThickness: 1,
        snapToStageCenter: true,
        snapToStageBorders: true,
        snapToShapes: true,
    });

    useEffect(() => {
        if (selectedId === imageItem.id && transformerRef.current && imageRef.current) {
            // Attach transformer to the image node
            transformerRef.current.nodes([imageRef.current]);
            transformerRef.current.getLayer()?.batchDraw();
        }
    }, [selectedId, imageItem.id]);

    // Calculate initial dimensions maintaining aspect ratio
    useEffect(() => {
        if (imageRef.current && image) {
            // If dimensions aren't set yet, use the natural size
            if (!imageItem.width || !imageItem.height) {
                const updatedItem = {
                    ...imageItem,
                    width: image.naturalWidth,
                    height: image.naturalHeight,
                };
                onChange(updatedItem);
            }
        }
    }, [image, imageItem, onChange]);

    const handleTransformEnd = (e: KonvaEventObject<Event>) => {
        handleSnappingResizeEnd(e);
        addToHistory();

        if (!imageRef.current) return;
        const node = imageRef.current;

        // Get the new position, scale, and rotation
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const rotation = node.rotation();
        const x = node.x();
        const y = node.y();

        // Update the layer with new properties
        setLayers((prevLayers) =>
            prevLayers.map((layer) =>
                layer.id === imageItem.id
                    ? ({
                          ...layer,
                          scaleX,
                          scaleY,
                          rotation,
                          x,
                          y,
                      } as ImageItem)
                    : layer,
            ),
        );
    };

    return (
        <>
            <Image
                ref={imageRef}
                id={imageItem.id}
                image={image}
                x={imageItem.x}
                y={imageItem.y}
                width={imageItem.width}
                height={imageItem.height}
                scaleX={imageItem.scaleX}
                scaleY={imageItem.scaleY}
                rotation={imageItem.rotation}
                draggable
                onDragStart={() => {
                    setIsDragging(true);
                    setSelectedId(imageItem.id);
                }}
                onDragMove={handleSnapping}
                onDragEnd={(e) => {
                    handleSnappingDragEnd(e);
                    setIsDragging(false);
                    addToHistory();
                    if (!imageRef.current) return;

                    // Update both the image item and layers
                    const newImageItem = {
                        ...imageItem,
                        x: imageRef.current.x(),
                        y: imageRef.current.y(),
                    };
                    onChange(newImageItem);
                    setLayers((prevLayers) =>
                        prevLayers.map((layer) =>
                            layer.id === imageItem.id ? (newImageItem as ImageItem) : layer,
                        ),
                    );
                }}
                onClick={() => setSelectedId(imageItem.id)}
                onTransform={handleSnappingResize}
                onTransformEnd={handleTransformEnd}
                opacity={isDragging ? 0.5 : 1}
            />
            {selectedId === imageItem.id && (
                <Transformer
                    ref={transformerRef}
                    padding={5}
                    rotateEnabled={true}
                    keepRatio={true}
                    enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right"]}
                    boundBoxFunc={(oldBox, newBox) => {
                        // Limit resize
                        const minWidth = 5;
                        const minHeight = 5;
                        const maxWidth = 1920; // Same as BASE_STAGE_WIDTH from parent
                        const maxHeight = 1080; // Based on 16:9 ratio
                        if (
                            newBox.width < minWidth ||
                            newBox.height < minHeight ||
                            newBox.width > maxWidth ||
                            newBox.height > maxHeight
                        ) {
                            return oldBox;
                        }
                        return newBox;
                    }}
                />
            )}
        </>
    );
};
